import React, { useEffect, useState, useRef } from "react";
import { api, formatApiError, fmtMXN } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Pizza, Plus, Image as ImageIcon, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function VariantesEditor({ value, onChange }) {
  const variantes = value || [];
  const addGrupo = () => onChange([...variantes, { nombre: "Nuevo grupo", obligatoria: false, multiple: false, opciones: [] }]);
  const updGrupo = (i, patch) => { const c = [...variantes]; c[i] = { ...c[i], ...patch }; onChange(c); };
  const delGrupo = (i) => onChange(variantes.filter((_, idx) => idx !== i));
  const addOpcion = (i) => { const c = [...variantes]; c[i].opciones = [...(c[i].opciones || []), { nombre: "Nueva", precio_extra: 0 }]; onChange(c); };
  const updOpcion = (i, j, patch) => { const c = [...variantes]; c[i].opciones[j] = { ...c[i].opciones[j], ...patch }; onChange(c); };
  const delOpcion = (i, j) => { const c = [...variantes]; c[i].opciones = c[i].opciones.filter((_, idx) => idx !== j); onChange(c); };

  return (
    <div className="space-y-3">
      {variantes.map((g, i) => (
        <Card key={i} className="border-dashed">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2">
              <Input value={g.nombre} onChange={(e) => updGrupo(i, { nombre: e.target.value })} placeholder="Nombre del grupo" />
              <Button size="sm" variant="ghost" onClick={() => delGrupo(i)}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="flex items-center gap-4 text-xs">
              <label className="flex items-center gap-2"><Switch checked={!!g.obligatoria} onCheckedChange={(v) => updGrupo(i, { obligatoria: v })} /> Obligatoria</label>
              <label className="flex items-center gap-2"><Switch checked={!!g.multiple} onCheckedChange={(v) => updGrupo(i, { multiple: v })} /> Múltiple</label>
            </div>
            <div className="space-y-1">
              {(g.opciones || []).map((o, j) => (
                <div key={j} className="flex items-center gap-2">
                  <Input value={o.nombre} onChange={(e) => updOpcion(i, j, { nombre: e.target.value })} className="flex-1" />
                  <Input type="number" step="0.01" value={o.precio_extra} onChange={(e) => updOpcion(i, j, { precio_extra: parseFloat(e.target.value) || 0 })} className="w-24" />
                  <Button size="sm" variant="ghost" onClick={() => delOpcion(i, j)}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => addOpcion(i)} className="w-full"><Plus className="h-3 w-3 mr-1" />Opción</Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button variant="outline" onClick={addGrupo} className="w-full"><Plus className="h-4 w-4 mr-1" />Agregar grupo de variantes</Button>
    </div>
  );
}

function ProductoDialog({ open, onOpenChange, producto, categorias, areas, acompanamientos, onSaved }) {
  const [form, setForm] = useState({ nombre: "", descripcion: "", precio_base: 0, categoria_id: "", area: "COCINA", variantes: [], acompanamiento_id: null, ingredientes_externos: [] });
  const fileInput = useRef();
  const [uploading, setUploading] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const defaultArea = (areas && areas[0]?.nombre) || "COCINA";
    if (producto) setForm({
      ...producto,
      acompanamiento_id: producto.acompanamiento_id ?? null,
      ingredientes_externos: producto.ingredientes_externos || [],
    });
    else setForm({ nombre: "", descripcion: "", precio_base: 0, categoria_id: categorias[0]?.id || "", area: defaultArea, variantes: [], acompanamiento_id: null, ingredientes_externos: [] });
    setPendingFile(null);
    setPreviewUrl(null);
  }, [producto, categorias, areas, open]);

  const uploadFor = async (prodId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post(`/productos/${prodId}/imagen`, fd, { headers: { "Content-Type": "multipart/form-data" } });
    return r.data;
  };

  const save = async () => {
    if (!form.nombre || form.precio_base === "" || form.precio_base === undefined || !form.categoria_id) {
      return toast.error("Completa nombre, precio y categoría");
    }
    setUploading(true);
    try {
      let savedId = producto?.id;
      const payload = { ...form, precio_base: parseFloat(form.precio_base) || 0 };
      // Permitir limpiar acomp con null explícito
      if (form.acompanamiento_id === "" || form.acompanamiento_id === "_none_") payload.acompanamiento_id = null;
      if (savedId) {
        await api.patch(`/productos/${savedId}`, payload);
      } else {
        const r = await api.post("/productos", payload);
        savedId = r.data.id;
      }
      // Upload pending image if any
      if (pendingFile && savedId) {
        await uploadFor(savedId, pendingFile);
      }
      toast.success(`Producto "${form.nombre}" ${producto?.id ? "actualizado" : "agregado"} correctamente.`);
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Selecciona un archivo de imagen"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Imagen mayor a 5MB"); return; }
    setPendingFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    // Si ya existe el producto, subir inmediatamente
    if (producto?.id) {
      setUploading(true);
      try {
        const r = await uploadFor(producto.id, file);
        setForm({ ...form, imagen_url: r.imagen_url, imagen_path: r.storage_path });
        toast.success("Imagen actualizada");
        setPendingFile(null);
        onSaved();
      } catch (err) { toast.error(formatApiError(err)); }
      finally { setUploading(false); }
    }
  };

  const displayImg = previewUrl || (form.imagen_url ? (form.imagen_url.startsWith("http") ? form.imagen_url : `${API}${form.imagen_url}`) : null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{producto?.id ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2"><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="producto-nombre-input" /></div>
            <div><Label>Precio base *</Label><Input type="number" step="0.01" value={form.precio_base} onChange={(e) => setForm({ ...form, precio_base: e.target.value })} data-testid="producto-precio-input" /></div>
            <div>
              <Label>Categoría *</Label>
              <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
                <SelectTrigger><SelectValue placeholder="Categoría" /></SelectTrigger>
                <SelectContent>
                  {categorias.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Área de preparación *</Label>
              <Select value={form.area} onValueChange={(v) => setForm({ ...form, area: v })}>
                <SelectTrigger data-testid="producto-area-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {(areas && areas.length > 0 ? areas : [{ nombre: "COCINA" }, { nombre: "BEBIDAS" }, { nombre: "FREIDORA" }]).map((a) => (
                    <SelectItem key={a.nombre} value={a.nombre}>{a.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Acompañamiento incluido</Label>
              <p className="text-[10px] text-muted-foreground mt-0.5 mb-1">Se agregará automáticamente sin costo cuando el producto se sume a una orden.</p>
              <Select
                value={form.acompanamiento_id || "_none_"}
                onValueChange={(v) => setForm({ ...form, acompanamiento_id: v === "_none_" ? null : v })}
              >
                <SelectTrigger data-testid="producto-acomp-select"><SelectValue placeholder="Sin acompañamiento" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none_">— Sin acompañamiento —</SelectItem>
                  {(acompanamientos || []).map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.nombre} <span className="text-muted-foreground text-xs">({a.area})</span></SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {(!acompanamientos || acompanamientos.length === 0) && (
                <p className="text-[10px] text-amber-500 mt-1">No hay acompañamientos activos. Créalos desde Menú → Acompañamientos.</p>
              )}
            </div>
            <div className="col-span-2"><Label>Descripción</Label><Textarea value={form.descripcion || ""} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
          </div>

          <div>
            <Label>Imagen del producto</Label>
            <div className="flex items-center gap-3 mt-1">
              {displayImg ? (
                <img src={displayImg} alt="" className="h-24 w-24 object-cover rounded-lg border border-border" />
              ) : (
                <div className="h-24 w-24 rounded-lg bg-secondary flex items-center justify-center"><ImageIcon className="h-8 w-8 text-muted-foreground" /></div>
              )}
              <div className="flex flex-col gap-2">
                <input ref={fileInput} type="file" accept="image/*" onChange={onFileChange} className="hidden" />
                <Button type="button" variant="outline" disabled={uploading} onClick={() => fileInput.current?.click()} data-testid="upload-imagen-btn">
                  {uploading ? "Subiendo…" : (form.imagen_url || previewUrl ? "Reemplazar imagen" : "Seleccionar imagen")}
                </Button>
                {pendingFile && !producto?.id && <span className="text-xs text-emerald-500">📎 {pendingFile.name} — se subirá al guardar</span>}
                {!pendingFile && <span className="text-[10px] text-muted-foreground">JPG/PNG/WEBP, máx 5MB</span>}
              </div>
            </div>
          </div>

          <div>
            <Label>Variantes</Label>
            <VariantesEditor value={form.variantes} onChange={(v) => setForm({ ...form, variantes: v })} />
          </div>

          <div className="border-t border-border pt-3" data-testid="ingredientes-externos-section">
            <div className="flex items-center justify-between mb-2 gap-2">
              <div>
                <Label className="text-sm font-bold">Ingredientes con preparación externa</Label>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Subtareas internas que se mandan a otra área al confirmar la orden. NO se ven en el flujo del mesero ni modifican el precio. (Distinto de Acompañamientos.)
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => setForm({
                  ...form,
                  ingredientes_externos: [
                    ...(form.ingredientes_externos || []),
                    { id: `tmp-${Math.random().toString(36).slice(2, 9)}`, nombre: "", area: (areas && areas[0]?.nombre) || "COCINA", cantidad: 1, activo: true, notas: "", orden: (form.ingredientes_externos?.length || 0) },
                  ],
                })}
                data-testid="ingrediente-add-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" />Agregar
              </Button>
            </div>
            {(form.ingredientes_externos || []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground italic">Sin ingredientes con preparación externa.</p>
            ) : (
              <div className="space-y-2">
                {(form.ingredientes_externos || []).map((ing, idx) => {
                  const updIng = (patch) => {
                    const c = [...form.ingredientes_externos];
                    c[idx] = { ...c[idx], ...patch };
                    setForm({ ...form, ingredientes_externos: c });
                  };
                  const delIng = () => {
                    const c = form.ingredientes_externos.filter((_, i) => i !== idx);
                    setForm({ ...form, ingredientes_externos: c });
                  };
                  return (
                    <div key={ing.id || idx} className={`rounded-lg border p-2 ${ing.activo === false ? "opacity-60 border-dashed" : "border-border"}`} data-testid={`ingrediente-row-${idx}`}>
                      <div className="grid grid-cols-12 gap-2 items-end">
                        <div className="col-span-5">
                          <Label className="text-[10px]">Nombre</Label>
                          <Input
                            value={ing.nombre || ""}
                            onChange={(e) => updIng({ nombre: e.target.value })}
                            placeholder="Ej. Dedos de queso"
                            data-testid={`ingrediente-nombre-${idx}`}
                          />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-[10px]">Área</Label>
                          <Select value={ing.area || ""} onValueChange={(v) => updIng({ area: v })}>
                            <SelectTrigger data-testid={`ingrediente-area-${idx}`}><SelectValue placeholder="Área" /></SelectTrigger>
                            <SelectContent>
                              {(areas && areas.length > 0 ? areas : [{ nombre: "COCINA" }, { nombre: "BEBIDAS" }, { nombre: "FREIDORA" }]).map((a) => (
                                <SelectItem key={a.nombre} value={a.nombre}>{a.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-2">
                          <Label className="text-[10px]">Cant. (×producto)</Label>
                          <Input
                            type="number"
                            min="1"
                            value={ing.cantidad ?? 1}
                            onChange={(e) => updIng({ cantidad: Math.max(1, parseInt(e.target.value) || 1) })}
                            data-testid={`ingrediente-cantidad-${idx}`}
                          />
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <Button type="button" size="icon" variant="ghost" className="text-destructive" onClick={delIng} data-testid={`ingrediente-del-${idx}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                        <div className="col-span-12">
                          <Label className="text-[10px]">Notas (opcional)</Label>
                          <Input
                            value={ing.notas || ""}
                            onChange={(e) => updIng({ notas: e.target.value })}
                            placeholder="Notas para el área que prepara…"
                            data-testid={`ingrediente-notas-${idx}`}
                          />
                        </div>
                        <div className="col-span-12 flex items-center gap-2 mt-0.5">
                          <label className="flex items-center gap-2 text-xs cursor-pointer">
                            <input
                              type="checkbox"
                              className="h-3.5 w-3.5 accent-primary"
                              checked={ing.activo !== false}
                              onChange={(e) => updIng({ activo: e.target.checked })}
                              data-testid={`ingrediente-activo-${idx}`}
                            />
                            {ing.activo === false ? "Inactivo" : "Activo"}
                          </label>
                          {ing.area && form.area && ing.area === form.area && (
                            <span className="text-[10px] text-amber-600 dark:text-amber-400">
                              ⓘ Misma área que el producto principal: NO se genera tarea separada.
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save} disabled={uploading} data-testid="save-producto-btn">{uploading ? "Guardando…" : "Guardar"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CategoriaDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ nombre: "", descripcion: "", orden: 0 });
  const save = async () => {
    if (!form.nombre) return toast.error("Nombre requerido");
    try { await api.post("/categorias", form); toast.success("Categoría creada"); onSaved(); onOpenChange(false); setForm({ nombre: "", descripcion: "", orden: 0 }); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader><DialogTitle>Nueva categoría</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Nombre *</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="cat-nombre-input" /></div>
          <div><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
          <div><Label>Orden</Label><Input type="number" value={form.orden} onChange={(e) => setForm({ ...form, orden: parseInt(e.target.value) || 0 })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={save}>Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function MenuAdmin() {
  const [productos, setProductos] = useState([]);
  const [cats, setCats] = useState([]);
  const [areas, setAreas] = useState([]);
  const [acompanamientos, setAcompanamientos] = useState([]);
  const [prodDialog, setProdDialog] = useState(false);
  const [catDialog, setCatDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  // Filtros
  const [q, setQ] = useState("");
  const [fCategoria, setFCategoria] = useState("ALL");
  const [fArea, setFArea] = useState("ALL");
  const [fEstado, setFEstado] = useState("ACTIVOS");
  const [fImagen, setFImagen] = useState("ALL");
  const [fVariantes, setFVariantes] = useState("ALL");

  const load = async () => {
    const [p, c, a, ac] = await Promise.all([
      api.get("/productos", { params: { only_active: false } }),
      api.get("/categorias"),
      api.get("/areas").catch(() => ({ data: [] })),
      api.get("/acompanamientos", { params: { only_active: true } }).catch(() => ({ data: [] })),
    ]);
    // Hide TEST_* products entirely from admin UI
    const visible = p.data.filter((x) => !/^TEST_/i.test(x.nombre || ""));
    setProductos(visible);
    setCats(c.data);
    setAreas(a.data || []);
    setAcompanamientos(ac.data || []);
  };
  useEffect(() => { load(); }, []);
  // Reload on sucursal change (multi-sucursal admin)
  useEffect(() => {
    const h = () => load();
    window.addEventListener("sucursal-changed", h);
    return () => window.removeEventListener("sucursal-changed", h);
  }, []);

  const toggleActive = async (p) => {
    try {
      if (p.active) { await api.delete(`/productos/${p.id}`); } else { await api.patch(`/productos/${p.id}`, { active: true }); }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const clearFilters = () => {
    setQ(""); setFCategoria("ALL"); setFArea("ALL"); setFEstado("ACTIVOS"); setFImagen("ALL"); setFVariantes("ALL");
  };
  const hasFilters = q || fCategoria !== "ALL" || fArea !== "ALL" || fEstado !== "ACTIVOS" || fImagen !== "ALL" || fVariantes !== "ALL";

  const filtered = productos.filter((p) => {
    if (fEstado === "ACTIVOS" && !p.active) return false;
    if (fEstado === "INACTIVOS" && p.active) return false;
    if (fCategoria !== "ALL" && p.categoria_id !== fCategoria) return false;
    if (fArea !== "ALL" && p.area !== fArea) return false;
    if (fImagen === "CON" && !p.imagen_url) return false;
    if (fImagen === "SIN" && p.imagen_url) return false;
    if (fVariantes === "CON" && (!p.variantes || p.variantes.length === 0)) return false;
    if (fVariantes === "SIN" && p.variantes && p.variantes.length > 0) return false;
    if (q.trim()) {
      const term = q.toLowerCase().trim();
      const inName = p.nombre?.toLowerCase().includes(term);
      const cat = cats.find((c) => c.id === p.categoria_id);
      const inCat = cat?.nombre?.toLowerCase().includes(term);
      if (!inName && !inCat) return false;
    }
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h2 className="text-3xl font-black tracking-tight">Menú</h2>
          <p className="text-sm text-muted-foreground mt-1">Productos, categorías, variantes e imágenes.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCatDialog(true)} data-testid="new-categoria-btn"><Plus className="h-4 w-4 mr-1" />Categoría</Button>
          <Button onClick={() => { setEditing(null); setProdDialog(true); }} data-testid="new-producto-btn"><Plus className="h-4 w-4 mr-1" />Nuevo producto</Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4 grid gap-3 md:grid-cols-2 lg:grid-cols-6">
          <div className="lg:col-span-2">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Buscar</Label>
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre o categoría…" data-testid="menu-search-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Categoría</Label>
            <Select value={fCategoria} onValueChange={setFCategoria}>
              <SelectTrigger data-testid="menu-filter-cat"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todas</SelectItem>
                {cats.map((c) => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Área</Label>
            <Select value={fArea} onValueChange={setFArea}>
              <SelectTrigger data-testid="menu-filter-area"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todas</SelectItem>
                {(areas && areas.length > 0 ? areas : [{ nombre: "COCINA" }, { nombre: "BEBIDAS" }, { nombre: "FREIDORA" }]).map((a) => (
                  <SelectItem key={a.nombre} value={a.nombre}>{a.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Estado</Label>
            <Select value={fEstado} onValueChange={setFEstado}>
              <SelectTrigger data-testid="menu-filter-estado"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ACTIVOS">Activos</SelectItem>
                <SelectItem value="INACTIVOS">Inactivos</SelectItem>
                <SelectItem value="ALL">Todos</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Imagen</Label>
            <Select value={fImagen} onValueChange={setFImagen}>
              <SelectTrigger data-testid="menu-filter-imagen"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="CON">Con imagen</SelectItem>
                <SelectItem value="SIN">Sin imagen</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="lg:col-span-5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Variantes</Label>
            <Select value={fVariantes} onValueChange={setFVariantes}>
              <SelectTrigger data-testid="menu-filter-variantes"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos</SelectItem>
                <SelectItem value="CON">Con variantes</SelectItem>
                <SelectItem value="SIN">Sin variantes</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            {hasFilters && (
              <Button variant="outline" onClick={clearFilters} className="w-full" data-testid="menu-clear-filters-btn">
                Limpiar filtros
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground">
        Mostrando <span className="font-bold text-foreground">{filtered.length}</span> de {productos.length} productos
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {filtered.map((p) => {
          let precioMin = p.precio_base || 0;
          const tipoT = (p.variantes || []).find((g) => g.obligatoria);
          if (tipoT && tipoT.opciones?.length) {
            const minExtra = Math.min(...tipoT.opciones.map((o) => o.precio_extra || 0));
            precioMin = (p.precio_base || 0) + minExtra;
          }
          const hasObligatoria = (p.variantes || []).some((g) => g.obligatoria);
          return (
          <Card key={p.id} className={p.active ? "" : "opacity-50"} data-testid={`producto-card-${p.id}`}>
            <CardContent className="p-4">
              <div className="aspect-square rounded-lg bg-secondary mb-3 overflow-hidden flex items-center justify-center">
                {p.imagen_url ? (
                  <img src={p.imagen_url.startsWith("http") ? p.imagen_url : `${API}${p.imagen_url}`} alt={p.nombre} className="w-full h-full object-cover" />
                ) : <Pizza className="h-12 w-12 text-muted-foreground" />}
              </div>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-bold leading-tight line-clamp-2" title={p.nombre}>{p.nombre}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {cats.find((c) => c.id === p.categoria_id)?.nombre || "-"} · {p.area}
                  </div>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    {!p.active && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-muted text-muted-foreground font-bold">Inactivo</span>}
                    {(p.variantes || []).length > 0 && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-secondary text-foreground/80 font-bold">Variantes</span>}
                  </div>
                </div>
                <div className="font-black text-primary whitespace-nowrap text-sm">{hasObligatoria ? `desde ${fmtMXN(precioMin)}` : fmtMXN(precioMin)}</div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button size="sm" variant="outline" className="flex-1" onClick={() => { setEditing(p); setProdDialog(true); }} data-testid={`edit-producto-${p.id}`}>
                  <Pencil className="h-3 w-3 mr-1" />Editar
                </Button>
                <Button size="sm" variant={p.active ? "ghost" : "default"} onClick={() => toggleActive(p)}>
                  {p.active ? "Desactivar" : "Activar"}
                </Button>
              </div>
            </CardContent>
          </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-12">
            {productos.length === 0 ? "Aún no hay productos." : "No se encontraron productos con esos filtros."}
          </div>
        )}
      </div>

      <ProductoDialog open={prodDialog} onOpenChange={setProdDialog} producto={editing} categorias={cats} areas={areas} acompanamientos={acompanamientos} onSaved={load} />
      <CategoriaDialog open={catDialog} onOpenChange={setCatDialog} onSaved={load} />
    </div>
  );
}
